class PeekCarousel extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    if (!this.slider) return;

    this.slides = Array.from(this.slider.querySelectorAll('.slider__slide'));
    if (this.slides.length < 1) return;

    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dots = Array.from(this.querySelectorAll('.slider-counter__link'));
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');

    this.currentIndex = 0;
    this.autoplayInterval = null;
    this.scrollDebounceTimer = null;
    this.resizeTimeout = null;

    this.boundHandlers = {
      prev: (e) => { e.preventDefault(); this.go(-1); this.startAutoplay(); },
      next: (e) => { e.preventDefault(); this.go(1); this.startAutoplay(); },
      dot: (i) => (e) => { e.preventDefault(); this.goTo(i); this.startAutoplay(); },
      scroll: this.onScroll.bind(this),
      pause: this.pauseAutoplay.bind(this),
      resume: this.startAutoplay.bind(this),
      resize: this.onResize.bind(this),
    };

    this.updateUI();
  }

  connectedCallback() {
    if (!this.slider || !this.slides.length) return;
    this.bindEvents();
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.goTo(0, false);
      this.startAutoplay();
    }));
  }

  disconnectedCallback() {
    this.pauseAutoplay();
    if (this.scrollDebounceTimer) clearTimeout(this.scrollDebounceTimer);
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    if (!this.slider) return;

    this.slider.removeEventListener('scroll', this.boundHandlers.scroll);
    this.removeEventListener('mouseenter', this.boundHandlers.pause);
    this.removeEventListener('mouseleave', this.boundHandlers.resume);
    this.removeEventListener('focusin', this.boundHandlers.pause);
    this.removeEventListener('focusout', this.boundHandlers.resume);
    window.removeEventListener('resize', this.boundHandlers.resize);
  }

  bindEvents() {
    if (this.prevButton) this.prevButton.addEventListener('click', this.boundHandlers.prev);
    if (this.nextButton) this.nextButton.addEventListener('click', this.boundHandlers.next);
    this.dots.forEach((dot, i) => dot.addEventListener('click', this.boundHandlers.dot(i)));

    this.slider.addEventListener('scroll', this.boundHandlers.scroll, { passive: true });
    this.addEventListener('mouseenter', this.boundHandlers.pause);
    this.addEventListener('mouseleave', this.boundHandlers.resume);
    this.addEventListener('focusin', this.boundHandlers.pause);
    this.addEventListener('focusout', this.boundHandlers.resume);
    window.addEventListener('resize', this.boundHandlers.resize);
  }

  getTargetFor(index) {
    const slide = this.slides[index];
    if (!slide) return 0;
    const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);

    if (index === 0) return 0;
    if (index === this.slides.length - 1) return maxScroll;

    const sliderWidth = this.slider.clientWidth;
    const slideWidth = slide.getBoundingClientRect().width;
    const ideal = slide.offsetLeft - (sliderWidth - slideWidth) / 2;
    return Math.max(0, Math.min(maxScroll, ideal));
  }

  goTo(index, smooth = true) {
    const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
    const target = this.getTargetFor(clamped);
    this.slider.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'auto' });
    this.currentIndex = clamped;
    this.updateUI();
  }

  go(delta) {
    this.goTo(this.currentIndex + delta);
  }

  onScroll() {
    if (this.scrollDebounceTimer) clearTimeout(this.scrollDebounceTimer);
    this.scrollDebounceTimer = setTimeout(() => this.recomputeCurrentIndex(), 80);
  }

  recomputeCurrentIndex() {
    let closest = 0;
    let minDist = Infinity;
    const sliderCenter = this.slider.scrollLeft + this.slider.clientWidth / 2;

    this.slides.forEach((slide, i) => {
      const slideCenter = slide.offsetLeft + slide.getBoundingClientRect().width / 2;
      const dist = Math.abs(slideCenter - sliderCenter);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });

    if (closest !== this.currentIndex) {
      this.currentIndex = closest;
      this.updateUI();
    }
  }

  updateUI() {
    if (this.currentPageElement) this.currentPageElement.textContent = this.currentIndex + 1;
    if (this.pageTotalElement) this.pageTotalElement.textContent = this.slides.length;

    this.dots.forEach((dot, i) => {
      const isActive = i === this.currentIndex;
      dot.classList.toggle('slider-counter__link--active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    this.toggleDisabled(this.prevButton, this.currentIndex <= 0);
    this.toggleDisabled(this.nextButton, this.currentIndex >= this.slides.length - 1);
  }

  toggleDisabled(button, disabled) {
    if (!button) return;
    if (disabled) button.setAttribute('disabled', 'disabled');
    else button.removeAttribute('disabled');
  }

  startAutoplay() {
    this.pauseAutoplay();
    if (this.slider.dataset.autoplay !== 'true') return;
    const speed = (parseFloat(this.slider.dataset.speed) || 5) * 1000;
    this.autoplayInterval = setInterval(() => {
      const next = this.currentIndex + 1;
      this.goTo(next >= this.slides.length ? 0 : next);
    }, speed);
  }

  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  onResize() {
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => this.goTo(this.currentIndex, false), 150);
  }
}

if (!customElements.get('peek-carousel')) {
  customElements.define('peek-carousel', PeekCarousel);
}
